import { faTrashCanXmark } from "@fortawesome/duotone-light-svg-icons"
import { faEye, faEyeSlash } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  JoonUIColor,
  Loader,
  Modal,
  ModalHeader,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { create } from "zustand"

import useAllQuestsMultiSelectStore from "./hooks/useAllQuestsMultiSelectStore"
import { useAllQuestsQuery } from "../../../networking/queries"
import {
  useDeleteQuestsMutation,
  useSetQuestActiveMutation,
  useSetQuestInactiveMutation,
} from "../../../networking/quests/mutations"
import { useGetQuestGroupSeriesIds } from "../useQuestGroups"

const AllQuestsMultiSelectActions = () => {
  const { selectedQuestSeriesIds, setSelectedQuestSeriesIds } =
    useAllQuestsMultiSelectStore()
  const deleteQuestsMutation = useDeleteQuestsMutation()
  const setInactiveMutation = useSetQuestInactiveMutation()
  const setActiveMutation = useSetQuestActiveMutation()
  const getQuestGroupSeriesIds = useGetQuestGroupSeriesIds()
  const { onOpen: openDeleteQuestModal } = useDeleteQuestModalStore()
  const { data: allQuests } = useAllQuestsQuery()

  if (selectedQuestSeriesIds.length === 0) return null

  const onSetAsInactive = async () => {
    const allQuestSeriesIds = selectedQuestSeriesIds.flatMap(
      getQuestGroupSeriesIds
    )
    await setInactiveMutation.mutateAsync(allQuestSeriesIds)
    setSelectedQuestSeriesIds([])
  }

  const onSetAsActive = async () => {
    const allQuestSeriesIds = selectedQuestSeriesIds.flatMap(
      getQuestGroupSeriesIds
    )
    await setActiveMutation.mutateAsync(allQuestSeriesIds)
    setSelectedQuestSeriesIds([])
  }

  const hasSelectedActiveQuests = selectedQuestSeriesIds.some((id) => {
    const quest = allQuests?.find((quest) => quest.id === id)
    return quest?.is_active
  })

  const hasSelectedInactiveQuests = selectedQuestSeriesIds.some((id) => {
    const quest = allQuests?.find((quest) => quest.id === id)
    return !quest?.is_active
  })

  const onDeleteQuests = async () => {
    openDeleteQuestModal()
  }

  return (
    <div
      className="flex-col flex items-center justify-center gap-2  bg-backgroundPrimary rounded-t-lg bottom-0 z-[1000]"
      style={{
        boxShadow: `0px 0px 10px 0px rgba(0, 0, 0, 0.1)`,
        width: `min(600px, 100%)`,
      }}
    >
      <div className="flex flex-row items-center justify-between w-full gap-2 border-b px-4 py-2 border-borderPrimary">
        <Typography variant="bodySmall">
          For the {selectedQuestSeriesIds.length} quest
          {selectedQuestSeriesIds.length > 1 ? "s" : ""} selected...
        </Typography>
        <TextButton onClick={() => setSelectedQuestSeriesIds([])}>
          <Typography variant="bodyXSmall" color={JoonUIColor.text.primary}>
            Unselect all
          </Typography>
        </TextButton>
      </div>
      <div className="flex flex-row items-center justify-center w-full gap-2 border-b p-4 border-borderPrimary">
        <button
          className="flex-col flex items-center justify-center gap-2 flex-1"
          onClick={onDeleteQuests}
          disabled={deleteQuestsMutation.isLoading}
        >
          {deleteQuestsMutation.isLoading ? (
            <Loader size={24} color={JoonUIColor.text.disabled} thickness={2} />
          ) : (
            <>
              <FontAwesomeIcon
                icon={faTrashCanXmark}
                color={JoonUIColor.text.disabled}
                fontSize={24}
              />
              <Typography
                variant="bodyXSmallBold"
                color={JoonUIColor.text.disabled}
              >
                Delete
              </Typography>
            </>
          )}
        </button>
        {hasSelectedActiveQuests && (
          <button
            className="flex-col flex items-center justify-center gap-2 flex-1"
            onClick={onSetAsInactive}
            disabled={
              setActiveMutation.isLoading || setInactiveMutation.isLoading
            }
          >
            {setInactiveMutation.isLoading ? (
              <Loader
                size={24}
                color={JoonUIColor.text.disabled}
                thickness={2}
              />
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  color={JoonUIColor.text.disabled}
                  fontSize={24}
                />
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.disabled}
                >
                  Set as inactive
                </Typography>
              </>
            )}
          </button>
        )}
        {hasSelectedInactiveQuests && (
          <button
            className="flex-col flex items-center justify-center gap-2 flex-1"
            onClick={onSetAsActive}
            disabled={
              setActiveMutation.isLoading || setInactiveMutation.isLoading
            }
          >
            {setActiveMutation.isLoading ? (
              <Loader
                size={24}
                color={JoonUIColor.text.disabled}
                thickness={2}
              />
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faEye}
                  color={JoonUIColor.text.disabled}
                  fontSize={24}
                />
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.disabled}
                >
                  Set as active
                </Typography>
              </>
            )}
          </button>
        )}
      </div>
      <DeleteQuestModal />
    </div>
  )
}

export default AllQuestsMultiSelectActions

interface DeleteQuestModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

const useDeleteQuestModalStore = create<DeleteQuestModalStore>((set) => ({
  isOpen: false,
  onOpen: () => set({ isOpen: true }),
  onClose: () => set({ isOpen: false }),
}))

const DeleteQuestModal = () => {
  const { isOpen, onClose } = useDeleteQuestModalStore()
  const getQuestGroupSeriesIds = useGetQuestGroupSeriesIds()
  const { selectedQuestSeriesIds, setSelectedQuestSeriesIds } =
    useAllQuestsMultiSelectStore()

  const deleteQuestsMutation = useDeleteQuestsMutation()

  const onClickDeleteQuests = async () => {
    const allQuestSeriesIds = selectedQuestSeriesIds.flatMap(
      getQuestGroupSeriesIds
    )
    await deleteQuestsMutation.mutateAsync(allQuestSeriesIds)
    setSelectedQuestSeriesIds([])
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} animate>
      <ModalHeader title="Delete Quest" onClose={onClose} showBorderBottom />
      <div className="flex flex-col items-center justify-center gap-2 p-6 pt-4">
        <Typography
          variant="body"
          color={JoonUIColor.text.primary}
          textAlign="left"
        >
          Are you sure you want to delete the selected Quest
          {selectedQuestSeriesIds.length > 1 ? "s" : ""}?
        </Typography>
        <Typography variant="body" textAlign="left">
          This action cannot be undone.
        </Typography>
        <div className="flex flex-row items-center justify-end gap-4 py-2 mt-2 w-full">
          <TextButton onClick={onClose}>
            <Typography
              variant="bodyBold"
              color={JoonUIColor.text.primaryAccent}
            >
              Cancel
            </Typography>
          </TextButton>
          <Button
            text="Delete Quests"
            onClick={onClickDeleteQuests}
            buttonType="redPrimary"
            isLoading={deleteQuestsMutation.isLoading}
          />
        </div>
      </div>
    </Modal>
  )
}
